import { Component } from "@angular/core";
import { ActivatedRoute,Router } from "@angular/router";
import { ToastService } from "angular-toastify";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { EdiMessage } from "@interfaces/ediMessage.interface";
import { EdiMessageService } from "@services/ediMessage.service";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { EdiMessageType } from "@enums/ediMessageType.enum";
import { EdiMessageProcessingStatus } from "@enums/ediMessageProcessingStatus.enum";
import { EdiMessageDirection } from "@enums/ediMessageDirection.enum";

@Component({
	moduleId: module.id,
	selector: "ediMessages-id",
	styleUrls: ["ediMessages-id.css"],
	templateUrl: "ediMessages-id.html"
})
export class EdiMessagesIdAdminComponent {
	ediTextCopied = false;
	ediTextPopupVisible = false;
	expandedRows = new Set<number>();
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	item: EdiMessage = new EdiMessage();
	itemId = 0;
	itemType = "Edi Message";
	messageDirections: any;
	messageTypes: any;
	parsedTextCopied = false;
	parsedTextPopupVisible = false;
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	shippingNoteSummary: {
		asnNumber: string;
		creationDate: string;
		estimatedArrivalDate: string;
		grossWeight: string;
		lineItems: {
			boxCode: string;
			boxCount: number;
			boxLabels: { label: string; qty: string }[];
			cumulativeQty: string;
			despatchedQty: string;
			emptyBoxCount: number;
			isReturnable: boolean;
			lidCode: string;
			lidCount: string;
			orderRef: string;
			packFactor: string;
			palletCode: string;
			palletCount: string;
			palletLabels: string[];
			partNumber: string;
		}[];
		netWeight: string;
		shippingReference: string;
	} = { asnNumber: "", creationDate: "", estimatedArrivalDate: "", grossWeight: "", lineItems: [], netWeight: "", shippingReference: "" };
	statuses: any;
	title: string = "View " + this.itemType;

	get messageTypeLabel(): string {
		const found = (this.messageTypes || []).find((t: any) => t.id === this.item.messageType);
		return found ? found.name : String(this.item.messageType ?? "—");
	}

	get messageDirectionLabel(): string {
		const found = (this.messageDirections || []).find((d: any) => d.id === this.item.messageDirection);
		return found ? found.name : String(this.item.messageDirection ?? "—");
	}

	get statusLabel(): string {
		const found = (this.statuses || []).find((s: any) => s.id === this.item.status);
		return found ? found.name : String(this.item.status ?? "—");
	}

	get isOutgoingShippingNote(): boolean {
		return this.item.messageDirection === EdiMessageDirection.Outgoing && this.item.messageType === EdiMessageType.ShippingNote;
	}

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private itemService: EdiMessageService) {
		this.messageDirections = GetPropertiesFromEnum(EdiMessageDirection);
		this.messageTypes = GetPropertiesFromEnum(EdiMessageType);
		this.statuses = GetPropertiesFromEnum(EdiMessageProcessingStatus);
		this.route.params.subscribe((params) => {
			this.itemId = params.id;
			this.getItem(params.id);
		});
	}

	get formattedEdiText(): string {
		return (this.item.ediMessageText || "")
			.split("'")
			.filter(s => s.trim().length > 0)
			.map(s => s.trim() + "'")
			.join("\n");
	}

	buildButtons() {
		this.headerPrimaryButtons = [
			{ method: "close", text: "Close" }
		];

		const tertiaryButtons: AdminHeaderButton[] = [];

		if (this.item.parsedEdiMessageContents) {
			tertiaryButtons.push({ method: "showParsedTextPopup", text: "View Parsed Message" });
		}

		if (this.item.ediMessageText) {
			tertiaryButtons.push({ method: "showEdiTextPopup", text: "View Formatted EDI Message" });
		}

		if (this.item.status === EdiMessageProcessingStatus.Deserialized) {
			tertiaryButtons.push({ method: "reprocessMessage", text: "Reprocess" });
		}

		this.headerTertiaryButtons = tertiaryButtons;
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}

	closeEdiTextPopup() {
		this.ediTextPopupVisible = false;
		this.ediTextCopied = false;
	}

	closeParsedTextPopup() {
		this.parsedTextPopupVisible = false;
		this.parsedTextCopied = false;
	}

	copyEdiText() {
		navigator.clipboard.writeText(this.formattedEdiText).then(() => {
			this.ediTextCopied = true;
			setTimeout(() => { this.ediTextCopied = false; }, 2500);
		});
	}

	copyParsedText() {
		navigator.clipboard.writeText(this.item.parsedEdiMessageContents || "").then(() => {
			this.parsedTextCopied = true;
			setTimeout(() => { this.parsedTextCopied = false; }, 2500);
		});
	}

	private formatEdiDate(raw: string): string {
		if (!raw || raw.length < 8) return raw;
		return `${raw.substring(6, 8)}/${raw.substring(4, 6)}/${raw.substring(0, 4)}`;
	}

	getItem(itemId: number) {
		this.itemService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.buildButtons();
					if (this.isOutgoingShippingNote) {
						this.parseShippingNoteSummary();
					}
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	private parseShippingNoteSummary() {
		const segments = (this.item.ediMessageText || "").split("'").filter(s => s.trim().length > 0);
		let asnNumber = "";
		let shippingReference = "";
		let creationDate = "";
		let estimatedArrivalDate = "";
		let grossWeight = "";
		let netWeight = "";
		const lineItems: {
			boxCode: string; boxCount: number; boxLabels: { label: string; qty: string }[];
			cumulativeQty: string; despatchedQty: string; emptyBoxCount: number;
			isReturnable: boolean; lidCode: string; lidCount: string;
			orderRef: string; packFactor: string; palletCode: string;
			palletCount: string; palletLabels: string[]; partNumber: string;
		}[] = [];

		let currentPartNumber = "";
		let currentOrderRef = "";
		let currentCumulativeQty = "";
		let currentPalletCount = "";
		let currentPalletCode = "";
		let currentLidCount = "";
		let currentLidCode = "";
		let currentBoxCount = 0;
		let currentBoxCode = "";
		let currentEmptyBoxCount = 0;
		let currentPackFactor = "";
		let currentIsReturnable = false;
		// "3" = palletised outer CPS, "4" = non-palletised outer CPS, "1" = inner CPS (boxes)
		let currentCpsType = "";
		let currentPalletLabels: string[] = [];
		let currentBoxLabels: { label: string; qty: string }[] = [];
		// Tracks the most recent QTY+52 so each GIR label shows the correct container qty
		let currentGirQty = "";

		const resetPackaging = () => {
			currentPalletCount = "";
			currentPalletCode = "";
			currentLidCount = "";
			currentLidCode = "";
			currentBoxCount = 0;
			currentBoxCode = "";
			currentEmptyBoxCount = 0;
			currentPackFactor = "";
			currentIsReturnable = false;
			currentPalletLabels = [];
			currentBoxLabels = [];
			currentGirQty = "";
		};

		for (const segment of segments) {
			const e = segment.trim().split("+");
			switch (e[0].trim()) {
				case "BGM":
					asnNumber = e[2] || "";
					break;
				case "CPS":
					// No parent (e[2] empty) = outer CPS: type 3 (palletised) or 4 (no pallet)
					// Has parent (e[2] set)  = inner CPS: type 1 (boxes inside a pallet)
					if (!e[2]) {
						resetPackaging();
						currentCpsType = e[3] || "";
					} else {
						currentCpsType = e[3] || "";
					}
					break;
				case "DTM": {
					const parts = (e[1] || "").split(":");
					if (parts[0] === "11") creationDate = this.formatEdiDate(parts[1]);
					else if (parts[0] === "132") estimatedArrivalDate = this.formatEdiDate(parts[1]);
					break;
				}
				case "GIR": {
					// GIR+3+{SSCC}:BJ' — qualifier 3 = transport label
					if ((e[1] || "") === "3") {
						const label = (e[2] || "").split(":")[0];
						if (label) {
							if (currentCpsType === "3") {
								// Outer palletised block — this is a pallet SSCC
								currentPalletLabels.push(label);
							} else {
								// Inner block (type 1) or non-palletised (type 4) — box SSCC
								currentBoxLabels.push({ label, qty: currentGirQty });
							}
						}
					}
					break;
				}
				case "MEA": {
					const weight = (e[3] || "").split(":")[1] || "";
					if (e[2] === "G") grossWeight = weight;
					else if (e[2] === "N") netWeight = weight;
					break;
				}
				case "PAC": {
					const packCode = e[3] || "";
					// e[2] contains ":37" for ALL auxiliary packaging (lids, empty boxes) per the JLR DESADV MIG.
					// Primary packaging (pallets, boxes of parts) will NOT have :37.
					const isAuxiliary = (e[2] || "").includes("37");
					const typeField = (e[4] || "").toUpperCase();
					// BPL generates: A:BASE (pallet), A:LID (lid), A:BOX (parts boxes), A:SUPPLIERDIV (empty boxes)
					// JLR standard also allows: A:PALLET*, A:LID*, A:BOX*, A:TRAY, A:STILLAGE etc.
					const isPallet = !isAuxiliary && (typeField.startsWith("A:BASE") || typeField.startsWith("A:PALLET") || packCode.toUpperCase().includes("BASE"));
					const isLid = isAuxiliary && (typeField.startsWith("A:LID") || packCode.toUpperCase().includes("LID"));
					const isEmptyBox = isAuxiliary && typeField.startsWith("A:SUPPLIERDIV");
					const isPartsBox = !isAuxiliary && typeField.startsWith("A:BOX");
					if (isPallet) { currentPalletCount = e[1] || ""; currentPalletCode = packCode; }
					else if (isLid) { currentLidCount = e[1] || ""; currentLidCode = packCode; }
					else if (isEmptyBox) { currentEmptyBoxCount += parseInt(e[1] || "0", 10); }
					else if (isPartsBox) { currentBoxCount += parseInt(e[1] || "0", 10); currentBoxCode = packCode; currentIsReturnable = (e[2] || "").includes("12"); }
					break;
				}
				case "RFF": {
					const parts = (e[1] || "").split(":");
					if (parts[0] === "CN") shippingReference = parts[1] || "";
					break;
				}
				case "LIN":
					currentPartNumber = (e[3] || "").split(":")[0];
					break;
				case "PIA":
					currentOrderRef = (e[2] || "").split(":")[0];
					break;
				case "QTY": {
					const parts = (e[1] || "").split(":");
					if (parts[0] === "52") {
						// Always update currentGirQty so each GIR label gets the right qty.
						// Only set packFactor from the first QTY+52 (full boxes).
						currentGirQty = parts[1] || "";
						if (!currentPackFactor) currentPackFactor = currentGirQty;
					} else if (parts[0] === "3") {
						currentCumulativeQty = parts[1] || "";
					} else if (parts[0] === "12") {
						lineItems.push({
							boxCode: currentBoxCode,
							boxCount: currentBoxCount,
							boxLabels: [...currentBoxLabels],
							cumulativeQty: currentCumulativeQty,
							despatchedQty: parts[1] || "",
							emptyBoxCount: currentEmptyBoxCount,
							isReturnable: currentIsReturnable,
							lidCode: currentLidCode,
							lidCount: currentLidCount,
							orderRef: currentOrderRef,
							packFactor: currentPackFactor,
							palletCode: currentPalletCode,
							palletCount: currentPalletCount,
							palletLabels: [...currentPalletLabels],
							partNumber: currentPartNumber,
						});
						currentPartNumber = "";
						currentOrderRef = "";
						currentCumulativeQty = "";
					}
					break;
				}
			}
		}

		this.shippingNoteSummary = { asnNumber, creationDate, estimatedArrivalDate, grossWeight, lineItems, netWeight, shippingReference };
	}

	reprocessMessage() {
		if (this.item.status !== EdiMessageProcessingStatus.Deserialized) {
			this.toastService.error("Cannot reprocess this message");
			return;
		}

		if (confirm("Attempt to reprocess message?") == false) {
			return;
		}

		this.itemService.reprocessEdiMessage(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Successfully submitted message for reprocessing");
					this.getItem(this.item.id);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	showEdiTextPopup() {
		this.ediTextCopied = false;
		this.ediTextPopupVisible = true;
	}

	showParsedTextPopup() {
		this.parsedTextCopied = false;
		this.parsedTextPopupVisible = true;
	}

	toggleRow(index: number) {
		if (this.expandedRows.has(index)) {
			this.expandedRows.delete(index);
		} else {
			this.expandedRows.add(index);
		}
	}
}
